import { useState, useEffect } from 'react';
import { get } from '../libs/Requests';
import { StoreProductDTO } from '../types/StoreProduct';
import { useLanguage } from './useLanguage';
import { IPromoCodeDTO } from '../../../types/PromoCodeDTO';

export interface ProductsDataHooksProps {
  productId: string;
  lang: string;
}

const cachedProductsMap = new Map<string, StoreProductDTO>();

export function useProductData(props: ProductsDataHooksProps) {
  const { country } = useLanguage();
  const [productData, setProductData] = useState<StoreProductDTO>({
    name: '',
    shortName: '',
    currency: '',
    tiers: []
  });

  useEffect(() => {
    const productUrl = `/product_tiers?lang=${props.lang}&product_short_name=${props.productId}&user_locale=${country}`;
    const cachedProductData = cachedProductsMap.get(productUrl);

    if (cachedProductData) {
      setProductData(cachedProductData);
    } else {
      const abortController = new AbortController();
      const signal = abortController.signal;

      get(productUrl, { signal })
        .then(async (response) => {
          const data = await response!.json();

          setProductData(data);

          cachedProductsMap.set(productUrl, data);
        })
        .catch(() => {});
    }
  }, []);

  function _findTier(seatsNumber: number) {
    const tier = productData?.tiers
      .filter((t) => {
        return (
          (!t.seatsRange!.to && seatsNumber >= t.seatsRange.from) ||
          (seatsNumber >= t.seatsRange.from && seatsNumber <= t.seatsRange!.to!)
        );
      })
      .pop();

    return tier;
  }

  function calculateYearlyPriceForGivenSeats(seatsNumber: number, promoDetails?: IPromoCodeDTO): string {
    const tier = _findTier(seatsNumber);

    if (!tier) {
      return '';
    }

    let price = parseInt(tier.priceByYear!) * seatsNumber;

    if (promoDetails) {
      if (promoDetails!.restrictions && promoDetails!.restrictions!.minimumAmount! / 100 <= price) {
        console.info('Promo code details', promoDetails);
        if (promoDetails.coupon!.percentOff) {
          price = price - (price * promoDetails.coupon!.percentOff) / 100;
        } else if (promoDetails.coupon!.amountOff) {
          price = price - promoDetails.coupon!.amountOff / 100;
        }
      }
    }
    price = Math.round(price);

    if (tier.currency === 'eur') {
      return `€${price} EUR`;
    }

    if (tier.currency === 'usd') {
      return `$${price} USD`;
    }

    return '';
  }

  function getCurrencySymbol() {
    if (productData.currency === 'usd') {
      return '$';
    }

    if (productData.currency === 'eur') {
      return '€';
    }

    return '';
  }

  function getTierPriceWithCurrency(seatsNumber: number): string {
    const tier = _findTier(seatsNumber);

    if (!tier) {
      return '';
    }

    if (tier.currency === 'eur') {
      return `€${tier.priceByMonth}`;
    }

    if (tier.currency === 'usd') {
      return `$${tier.priceByMonth}`;
    }

    return tier.priceByMonth!;
  }

  function getTierPrice(seatsNumber: number): string {
    const tier = _findTier(seatsNumber);

    if (!tier) {
      return '';
    }

    return tier.priceByMonth!;
  }

  function getTierName(seatsNumber: number): string {
    const tier = _findTier(seatsNumber);

    if (!tier) {
      return '';
    }

    return tier?.seatsRange.to
      ? `${tier?.seatsRange.from}-${tier?.seatsRange.to} Users Tier`
      : `${tier?.seatsRange.from}+ Users Tier`;
  }

  return {
    productData,
    getTierName,
    getTierPrice,
    calculateYearlyPriceForGivenSeats,
    getCurrencySymbol,
    getTierPriceWithCurrency
  };
}
